import { statusColors } from 'utils/styleConstants';

// This function is used to create the options (label & value) for the dropdowns
const buildOptionsConcatLabel = (values: any[], labels: string[], value: string) => {
  if (values === null || !values.length) return [];
  return values.map((ele) => {
    return {
      label: labels
        .map((label) => ele[label])
        .filter((label) => label)
        .join(' - '),
      value: ele[value],
    };
  });
};
// This function is used to create the options (label & value) for the dropdowns
const buildOptions = (values: any[], label: string, value: string, concatLabelValue?: boolean) => {
  if (values === null || !values.length) return [];

  return values.map((ele) => {
    return {
      label: concatLabelValue ? `${ele[value]} - ${ele[label]}` : ele[label],
      value: ele[value],
    };
  });
};

// this functions is used to create the options (label & value) for the dropdowns
const buildOptionsMaps = (
  values: any[],
  label: string,
  value: string,
  concatLabelValue?: boolean,
): Map<string, string> => {
  const map = new Map();
  values.forEach((ele) => {
    map.set(
      String(ele[value]),
      concatLabelValue || (label === 'description' && value === 'code') ? `${ele[value]} - ${ele[label]}` : ele[label],
    );
  });
  return map;
};
/**
 * This function returns a boolean to handle the visibility of an Input
 * valued by comparing the confidece and the threshold (confidence < threshold)
 * except when the filter is not active or the field is mandatory
 * @param { boolean } globalSetting filter status
 * @param { number } confidence input's value accuracy assigned by the ML
 * @param { number } threshold
 * @param { number } obbl status number value declaring the if the field is required
 * @param { string } value text value of the field
 * @return { boolean }
 */
const isFieldVisible = (
  globalSetting: boolean,
  confidence: number,
  threshold: number,
  obbl: number,
  value?: string,
) => {
  if (!globalSetting) return true;

  // obbligatorio
  if (obbl > 0) return true;

  // normal field
  if (obbl === 0) {
    return confidence < threshold;
  }

  // warning
  return obbl < 0 && value ? true : false;
};

const getInputStatusColor = (id: number, status: any, readonly?: boolean) => {
  if (readonly) return undefined;

  if (status?.length) {
    const found = status.find(({ idField }: any) => idField === id);
    const statusValue = found?.status;

    switch (statusValue) {
      case 1:
        return statusColors.error;
      case 2:
        return statusColors.warning;
      case 3:
        return statusColors.success;
      default:
        return undefined;
    }
  }
};

const getInputStatusMessage = (id: number, status: any, readonly?: boolean) => {
  if (readonly) return undefined;

  if (status?.length) {
    const found = status.find(({ idField }: any) => idField === id);
    return found?.messageField;
  }
};

const removeDefaultValue = (values: any[], label: string) => {
  return values.filter((value) => value[label] !== 0);
};

const getLabelTypeByKey = (key: number) => {
  const keyMap = {
    0: 'code-description',
    1: 'code',
    2: 'description',
  };

  const labelType = keyMap[key as keyof typeof keyMap];
  return labelType || 'code-description';
};

const buildSingleOptionValue = (item: any, label: string, value: string, concatLabelValue?: boolean) => {
  if (!item) return null;

  return {
    label: concatLabelValue ? `${item[value]} - ${item[label]}` : item[label],
    value: item[value],
  };
};

export const buildCurrentValue = ({
  fieldContent,
  hTableValues,
  optionFormat,
}: {
  fieldContent: any;
  hTableValues: any[];
  optionFormat: number;
}) => {
  if (fieldContent === undefined || fieldContent === null || fieldContent === '') {
    return null;
  }

  const found = hTableValues?.find((el) => el.code === fieldContent);

  const labelType = getLabelTypeByKey(optionFormat);
  const useDescription = labelType.includes('description');
  const concatLabelValue = labelType === 'code-description';

  let newValue: { value: string; label: string };

  if (found) {
    const { code, description } = found;
    const labelKey = useDescription ? 'description' : 'code';
    newValue = buildSingleOptionValue({ code, description }, labelKey, 'code', concatLabelValue) as {
      value: string;
      label: string;
    };
  } else {
    newValue = { value: fieldContent, label: fieldContent };
  }

  return newValue;
};

const truncateFilterSuffix = (filterName: string): string => {
  // List of common suffixes to remove (case-insensitive)
  const suffixes = ['Equals', 'Like', 'In', 'Range'];

  // Try to match and remove any suffix
  for (const suffix of suffixes) {
    const regex = new RegExp(suffix + '$', 'i');
    if (regex.test(filterName)) {
      return filterName.replace(regex, '');
    }
  }

  return filterName;
};

export const getBaseFilterName = (filter: any): string | null => {
  // If filter has inputCombinedOptions, get filterName from the first option
  if (filter.inputProp?.inputCombinedOptions && filter.inputProp.inputCombinedOptions.length > 0) {
    const firstOption = filter.inputProp.inputCombinedOptions[0];
    if (firstOption.filterName) {
      return truncateFilterSuffix(firstOption.filterName);
    }
  }

  // If filter has a direct filterName array
  if (Array.isArray(filter.filterName) && filter.filterName.length > 0) {
    return truncateFilterSuffix(filter.filterName[0]);
  }

  // If filter has a direct filterName string
  if (typeof filter.filterName === 'string') {
    return truncateFilterSuffix(filter.filterName);
  }

  return null;
};

export default {
  buildOptionsConcatLabel,
  buildOptionsMaps,
  buildOptions,
  isFieldVisible,
  getInputStatusMessage,
  getInputStatusColor,
  removeDefaultValue,
  getLabelTypeByKey,
  buildSingleOptionValue,
  buildCurrentValue,
  getBaseFilterName,
};
